import { BybProgressBar } from "@Components/common/byb-ds/cards/styled";
import UserStatsIcon from "@Containers/mobile/helpers/get-user-stats-icon";
import UserProgressStats from "@Enums/UserProgressStats";
import useI18n from "@i18n";
import { LinearProgress, linearProgressClasses, Skeleton, Stack, styled, Typography, useTheme } from "@mui/material";
import { isNumber } from "lodash-es";

interface IBybStatsCard {
  stat: UserProgressStats;
  value: number | null | undefined;
  target?: number | null | undefined;
  compact?: boolean;
}

const StatProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.dsWarning.v200,
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.dsWarning.v400,
  },
}));

const BybStatsCard = ({ stat, value, target, compact = false }: IBybStatsCard) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const progressPercentage = value && target ? Math.min(100, (value / target) * 100) : 0;

  if (!isNumber(value)) {
    return (
      <Stack borderRadius={3} padding={2} spacing={3} border={`2px solid ${theme.palette.dsSurface.v300}`}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Skeleton variant="rounded" width={35} height={35} />
          <Skeleton variant="text" width={"40%"} height={35} />
        </Stack>

        <Stack spacing={1}>
          <Stack spacing={0.5}>
            <Skeleton variant="rounded" width={40} height={30} />
            <Skeleton variant="rounded" width={60} height={10} />
          </Stack>
          {!compact && <Skeleton variant="rounded" width={"100%"} height={10} />}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack borderRadius={3} padding={2} spacing={3} border={`2px solid ${theme.palette.dsSurface.v300}`}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <UserStatsIcon userStat={stat} size={6} />
        <Typography variant="textBase500" color={theme.palette.text.secondary}>
          {i18n(`stats.${stat}.label`)}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        <Stack spacing={0.5}>
          <Typography variant="text1xl600">
            {value.toFixed(0)} {target ? `/${target}` : ""}
          </Typography>
          <Typography variant="textS500" color={theme.palette.text.secondary}>
            {i18n(`stats.${stat}.subtitle`)}
          </Typography>
        </Stack>
        {!compact && <BybProgressBar variant="determinate" value={progressPercentage} metricType={stat} />}
      </Stack>
    </Stack>
  );
};

export default BybStatsCard;
